// import Cookie from 'js-cookie';
import { clear as clearSessionStorage } from '@/lib/storage';
import UserException from '@/lib/UserException';
import { isValidString } from '@/utilities/validators';
import errorMessages from '@/configs/errorMessages';
import {
  loginVerify,
  logout,
  getLoginStatus,
  getMemberAreaData,
  getContactData,
  getRecurringDonations,
  phoneSMSVerificationSend,
  phoneSMSVerify,
  resetPassword,
  resendPaymentReceipt,
  resendPaymentInfo,
} from '@/api';

const state = {
  fetching: false,
  fetched: false,
  loginStatus: null, // 登入方式
  memberAreaData: null,
  memberContactData: {},
  memberRecurringData: [],
};

const mutations = {
  fetchingStart(state) {
    state.fetching = true;
  },
  fetchingSuccess(state) {
    state.fetching = false;
    state.fetched = true;
  },
  fetchingError(state) {
    state.fetching = false;
    state.fetched = true;
  },
  updateLoginStatus(state, payload) {
    state.loginStatus = payload;
  },
  updateMemberAreaData(state, payload) {
    state.memberAreaData = payload;
  },
  updateMemberContactData(state, payload) {
    state.memberContactData = payload;
  },
  updateMemberRecurringData(state, payload) {
    state.memberRecurringData = payload;
  },
  clearMemberData(state) {
    state.memberAreaData = null;
    state.memberContactData = {};
    state.memberRecurringData = [];
    clearSessionStorage();
  },
};

const actions = {
  async login({ commit }, {
    email,
    password,
  }) {
    try {
      const options = {
        Email: email,
        Password: password,
      };
      commit('fetchingStart');
      const resp = await loginVerify(options);
      const { rtnStatus } = resp.data;
      if(rtnStatus !== 'Success') throw new Error('login fail');
      commit('updateLoginStatus', 'email');
      commit('fetchingSuccess');
      return true;
    } catch (err) {
      console.error(err);
      commit('fetchingError');
      return false;
    }
  },
  async getLoginStatus({ commit }) {
    try {
      commit('fetchingStart');
      const resp = await getLoginStatus();
      if(resp.status !== 200) throw new Error(errorMessages.defaultMessage);
      if(!resp.data?.rtnStatus
        && resp.data?.accountType !== 'phone' // 以電話驗證登入時，rtnStatus 爲 false
      ) {
        commit('updateLoginStatus', null);
        commit('fetchingSuccess');
        return false;
      }
      commit('updateLoginStatus', resp.data.accountType);
      commit('fetchingSuccess');
      return true;
    } catch (err) {
      console.error(err);
      commit('fetchingError');
      return false;
    }
  },
  async getMemberAreaData({ commit }) {
    try {
      commit('fetchingStart');
      const resp = await getMemberAreaData();
      if(resp.status !== 200) throw new Error(errorMessages.defaultMessage);
      if(!resp.data?.done) throw new Error('fail');
      commit('updateMemberAreaData', resp.data.records);
      commit('fetchingSuccess');
      return resp.data.records;
    } catch (err) {
      console.error('getMemberAreaData fail: ', err);
      commit('updateMemberAreaData', []);
      commit('fetchingError');
      return [];
    }
  },
  async getContactData({ commit }) {
    try {
      commit('fetchingStart');
      const resp = await getContactData(true);
      if(resp.status !== 200) throw new Error(errorMessages.defaultMessage);
      const {
        contactCreditCardExpire,
        contactCreditCardNo,
        contactEmail,
        contactEtaxCheck,
        contactFirstName,
        contactGroupTitle,
        contactHomeAddress,
        contactInvoiceType,
        contactLastName,
        contactName,
        contactPhone,
        loginAccountEmail,
      } = resp.data;
      commit('updateMemberContactData', {
        contactCreditCardExpire,
        contactCreditCardNo,
        contactEmail,
        contactEtaxCheck,
        contactFirstName,
        contactGroupTitle,
        contactHomeAddress,
        contactInvoiceType,
        contactLastName,
        contactName,
        contactPhone,
        loginAccountEmail,
      });
      commit('fetchingSuccess');
      return true;
    } catch (err) {
      console.error('getMemberAreaData fail: ', err);
      commit('fetchingError');
      return false;
    }
  },
  async getRecurringDonations({ commit }) {
    try {
      commit('fetchingStart');
      const resp = await getRecurringDonations(true);
      if(resp.status !== 200) throw new Error(errorMessages.defaultMessage);
      commit('fetchingSuccess');
      commit('updateMemberRecurringData', resp.data);
      return true;
    } catch (err) {
      console.error('getRecurringDonations fail: ', err);
      commit('fetchingError');
      return false;
    }
  },
  async phoneSMSVerificationSend({ commit }, {
    firstName,
    lastName,
    tel,
  }) {
    try {
      if(!isValidString(firstName)
        || !isValidString(lastName)
        || !isValidString(tel)
      ) {
        throw new UserException('填入欄位內容有誤，請重新確認。');
      }

      commit('fetchingStart');
      const resp = await phoneSMSVerificationSend({
        mobilePhone: tel,
        firstName,
        lastName,
      });
      if(resp.status !== 200) throw new UserException(errorMessages.defaultMessage);
      if(resp.data?.rtnStatus !== 'Success') throw new UserException(resp?.data?.msg || errorMessages.defaultMessage);
      commit('fetchingSuccess');
      alert(resp.data?.msg || '已寄送驗證碼，請查看手機簡訊。若無法成功接收驗證碼，請再次嘗試，或寄信至 fin@teach4taiwan.org 詢問。');
      return true;
    } catch (err) {
      console.error('phoneSMSVerificationSend fail: ', err);
      alert(err.msg);
      commit('fetchingError');
      return false;
    }

  },
  async phoneSMSVerify({ commit }, {
    verificationCode,
  }) {
    try {
      if(!isValidString(verificationCode)) throw new UserException('驗證碼錯誤，請重新確認。');

      commit('fetchingStart');
      const resp = await phoneSMSVerify({ verificationCode });
      if(resp.status !== 200) throw new UserException(errorMessages.defaultMessage);
      if(resp.data?.rtnStatus !== 'Success') throw new UserException('驗證碼錯誤，請重新確認。');
      commit('fetchingSuccess');
      return true;
    } catch (err) {
      console.error('phoneSMSVerify fail: ', err);
      alert(err.msg);
      commit('fetchingError');
      return false;
    }
  },
  async resetPassword({ commit }, {
    email,
  }) {
    try {
      commit('fetchingStart');
      const resp = await resetPassword({ Email: email });
      if(resp.status !== 200) throw new Error(errorMessages.defaultMessage);
      if(resp.data?.rtnStatus !== '') throw new Error('fail');
      commit('fetchingSuccess');
      return true;
    } catch (err) {
      console.error('resetPassword fail: ', err);
      commit('fetchingError');
      return false;
    }
  },
  async logout({ commit }) {
    try {
      commit('fetchingStart');
      const resp = await logout();
      if(resp.status !== 200) throw new Error(errorMessages.defaultMessage);
      commit('fetchingSuccess');
      commit('clearMemberData');
      commit('updateLoginStatus', null);
      return true;
    } catch (err) {
      console.error('logout fail: ', err);
      commit('fetchingError');
      return false;
    }
  },
  async resendPaymentReceipt({ commit }, id) {
    try {
      commit('fetchingStart');
      const resp = await resendPaymentReceipt({ Id: id });
      if(resp.status !== 200) throw new Error(errorMessages.defaultMessage);
      if(resp.data?.rtnStatus !== 'Success') throw new Error('fail');
      commit('fetchingSuccess');
      return true;
    } catch (err) {
      console.error('resendPaymentReceipt fail: ', err);
      commit('fetchingError');
      return false;
    }
  },
  async resendPaymentInfo({ commit }, id) {
    try {
      commit('fetchingStart');
      const resp = await resendPaymentInfo({ Id: id });
      if(resp.status !== 200) throw new Error(errorMessages.defaultMessage);
      if(resp.data?.rtnStatus !== 'Success') throw new Error('fail');
      commit('fetchingSuccess');
      return true;
    } catch (err) {
      console.error('resendPaymentInfo fail: ', err);
      commit('fetchingError');
      return false;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
